import { useState, useCallback, useMemo, ChangeEvent } from 'react';
import {
  FaqDocument,
  isMaintenanceGuide,
  getDisplayQuestion,
  getDisplayAnswer,
} from 'src/components/Faqs/types';

export interface UseFaqSearchReturn {
  searchQuery: string;
  filteredFaqs: FaqDocument[];
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  clearSearch: () => void;
}

const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

const useFaqSearch = (faqs: FaqDocument[]): UseFaqSearchReturn => {
  const [searchQuery, setSearchQuery] = useState('');

  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.trim());
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const filteredFaqs: FaqDocument[] = useMemo(() => {
    if (!searchQuery) {
      return faqs;
    }

    const lowerQuery: string = searchQuery.toLowerCase();

    return faqs.filter((faq: FaqDocument) => {
      const searchableFields: string[] = [
        getDisplayQuestion(faq),
        stripHtml(getDisplayAnswer(faq)),
        faq.itemType || '',
        faq.itemName || '',
      ];

      if (isMaintenanceGuide(faq)) {
        searchableFields.push(faq.type);
      } else {
        const typeCategories: string = faq.type.join(' ');
        searchableFields.push(typeCategories);
      }

      return searchableFields.some((field: string) =>
        field.toLowerCase().includes(lowerQuery)
      );
    });
  }, [faqs, searchQuery]);

  return {
    searchQuery,
    filteredFaqs,
    onSearch,
    clearSearch,
  };
};

export default useFaqSearch;
